import {
  AddOutlined,
  ControlPointDuplicateOutlined,
  DeleteOutlined,
  DeviceHubOutlined,
  ElectricMeterOutlined,
  OutputOutlined,
  SettingsOutlined,
} from '@mui/icons-material'
import { Box, Button, Chip, Stack } from '@mui/material'
import type { GridColDef } from '@mui/x-data-grid'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import CustomDataGrid from '@/components/dataDisplay/CustomDataGrid'
import CustomTypography from '@/components/dataDisplay/CustomTypography'
import { AppConfigPowerMeterDrawer } from '@/features/controller/configuration/components/drawers/AppConfigPowerMeterDrawer'
import { ModbusMeterSettingsDrawer } from '@/features/controller/configuration/components/drawers/ModbusMeterSettingsDrawer'
import { TillquistPowerMeterDrawer } from '@/features/controller/configuration/components/drawers/TillquistPowerMeterDrawer'
import type { NodeActionsProps } from '@/features/controller/configuration/components/tree/NodeActions'
import { NodeActions, NodeMenuItemType } from '@/features/controller/configuration/components/tree/NodeActions'
import { NodeErrors } from '@/features/controller/configuration/components/tree/NodeErrors'
import { useDrawerContent } from '@/features/controller/configuration/hooks/useDrawerContent'
import type { PowerMeter } from '@/features/controller/configuration/models/appConfigSchema'
import type {
  DeviceConnectionNode,
  DeviceNode,
  ExternalModbusDeviceNode,
  LegacyModbusPowerMeterNode,
} from '@/features/controller/configuration/models/controllerConfigTree'
import {
  DeviceConnectionNodeType,
  DeviceNodeType,
} from '@/features/controller/configuration/models/controllerConfigTree'
import type {
  ControllerConfigurationModel,
  ControllerConfigurationModelUpdate,
} from '@/features/controller/configuration/models/controllerConfiguration'
import { MODBUS_METER_DEFAULTS } from '@/features/controller/configuration/models/raspiProperties/defaults'
import { isTillquistEnabled } from '@/features/controller/configuration/models/raspiProperties/tillquist'
import type { TillquistConfig } from '@/features/controller/configuration/models/raspiProperties/types'
import {
  getDevicesForConnectionNode,
  makeLegacyModbusMeterNode,
} from '@/features/controller/configuration/utils/controllerConfigTreeMaker'
import {
  describeExternalModbusDevice,
  ExternalModbusDeviceIcon,
  getExternalModbusDeviceIcon,
  NW_BATTERY_ICON_URL,
} from '@/features/controller/configuration/utils/externalModbusDevices'
import {
  describeAppConfigPowerMeterProperties,
  describeModbusMeterProperties,
  describeTillquistProperties,
  nextAppConfigPowerMeterId,
} from '@/features/controller/configuration/utils/powerMeters'

interface DeviceConfigurationProps {
  configuration: ControllerConfigurationModel
  connectionNode: DeviceConnectionNode
  onConfigurationUpdated: (configurationUpdate: ControllerConfigurationModelUpdate) => void
}

const UPDATE_COMMENT = 'Update made in configuration UI'

export function DeviceConfiguration({
  configuration,
  connectionNode,
  onConfigurationUpdated,
}: Readonly<DeviceConfigurationProps>) {
  const { t } = useTranslation()
  const tillquistDrawer = useDrawerContent()
  const appConfigPowerMeterDrawer = useDrawerContent()
  const modbusMeterDrawer = useDrawerContent<LegacyModbusPowerMeterNode>()

  const isBasicSerial = connectionNode.type === DeviceConnectionNodeType.BasicSerial
  const isLegacySerial = connectionNode.type === DeviceConnectionNodeType.LegacySerial
  const supportsModbus =
    connectionNode.type === DeviceConnectionNodeType.LegacySerial ||
    connectionNode.type === DeviceConnectionNodeType.LegacyModbusTcp ||
    connectionNode.type === DeviceConnectionNodeType.NamedModbusTcp ||
    connectionNode.type === DeviceConnectionNodeType.NamedModbusSerial

  const deviceNodes = useMemo(
    () => getDevicesForConnectionNode(connectionNode, configuration),
    [configuration, connectionNode],
  )
  const hasTillquistPowerMeterConfigured =
    configuration.raspiPropertiesConfig !== null &&
    isTillquistEnabled(configuration.raspiPropertiesConfig.tillquistConfig)
  // app.json holds one meter, which lands under one of the two serial port nodes, so the button is offered
  // where it would land: beside Tillquist as its additional settings, otherwise on the basic devices port
  const canAddAppConfigPowerMeter =
    (configuration.appConfig?.powerMeters?.length ?? 0) === 0 &&
    (hasTillquistPowerMeterConfigured ? isLegacySerial : isBasicSerial)
  const canAddTillquist = isLegacySerial && !hasTillquistPowerMeterConfigured
  const canAddDevice = canAddTillquist || canAddAppConfigPowerMeter || supportsModbus

  function onTillquistConfigurationUpdated(updatedTillquistConfig: TillquistConfig) {
    tillquistDrawer.close()
    onConfigurationUpdated({
      updateRaspiPropertiesConfig: (draft) => {
        draft.tillquistConfig = updatedTillquistConfig
      },
      comment: UPDATE_COMMENT,
    })
  }

  function onModbusMeterConfigurationUpdated(update: LegacyModbusPowerMeterNode) {
    modbusMeterDrawer.close()
    onConfigurationUpdated({
      updateRaspiPropertiesConfig: (draft) => {
        const conn = draft.legacyModbusConnections.find((conn) => conn.id === update.connectionId)
        if (conn) {
          const found = conn.meters.find((meter) => meter.number === update.config.number)
          if (found) {
            Object.assign(found, update.config)
          } else {
            conn.meters.push(update.config)
          }
        } else {
          console.error('Error adding new Modbus meter, no connection with id: ' + update.connectionId)
        }
      },
      comment: UPDATE_COMMENT,
    })
  }

  function removeModbusMeter(row: LegacyModbusPowerMeterNode) {
    onConfigurationUpdated({
      updateRaspiPropertiesConfig: (draft) => {
        const conn = draft.legacyModbusConnections.find((conn) => conn.id === row.connectionId)
        if (conn) {
          const found = conn.meters.findIndex((meter) => meter.number === row.config.number)
          if (found > -1) {
            conn.meters.splice(found, 1)
          }
        } else {
          console.error('Error removing Modbus meter, no connection with id: ' + row.connectionId)
        }
      },
      comment: UPDATE_COMMENT,
    })
  }

  function nextLegacyModbusMeterNumber() {
    let next = configuration.raspiPropertiesConfig!.legacyModbusMeters.highestMeterNumber + 1
    if (Number.isNaN(next) || next === Number.NEGATIVE_INFINITY) {
      next = 1
    }
    onConfigurationUpdated({
      updateRaspiPropertiesConfig: (draft) => {
        draft.legacyModbusMeters.highestMeterNumber = next
      },
      comment: UPDATE_COMMENT,
    })
    return next
  }

  function cloneModbusMeter(row: LegacyModbusPowerMeterNode) {
    modbusMeterDrawer.show(
      makeLegacyModbusMeterNode(connectionNode, {
        ...row.config,
        number: nextLegacyModbusMeterNumber(),
      }),
    )
  }

  function addNewModbusMeter() {
    modbusMeterDrawer.show(
      makeLegacyModbusMeterNode(connectionNode, {
        ...MODBUS_METER_DEFAULTS,
        number: nextLegacyModbusMeterNumber(),
      }),
    )
  }

  function onAppConfigPowerMeterUpdated(updatedPowerMeterConfig: Omit<PowerMeter, 'id'>) {
    appConfigPowerMeterDrawer.close()
    const existingId = configuration.appConfig?.powerMeters?.[0]?.id
    const id = existingId ?? nextAppConfigPowerMeterId(configuration.appConfig?.powerMeters ?? [])
    onConfigurationUpdated({
      updateAppConfig: (draft) => {
        draft.powerMeters = [{ ...updatedPowerMeterConfig, id } as PowerMeter]
      },
      comment: UPDATE_COMMENT,
    })
  }

  function isTillquistSettingsNode(row: DeviceNode) {
    return row.type === DeviceNodeType.AppConfigPowerMeter && row.isTillquistSettingsNode
  }

  function renderExternalDeviceIcon(node: ExternalModbusDeviceNode) {
    const title = t('controllers.configuration.device_connections.external_device.icon_title')
    switch (getExternalModbusDeviceIcon(node.config.family)) {
      case ExternalModbusDeviceIcon.battery:
        return <img alt={title} height={24} src={NW_BATTERY_ICON_URL} width={24} />
      case ExternalModbusDeviceIcon.meter:
        // A metering channel measures, like the power meters above it, so it reads the same
        return <ElectricMeterOutlined color="action" titleAccess={title} />
      case ExternalModbusDeviceIcon.steering:
        // Steering is the opposite direction from metering: a command out rather than a reading in
        return <OutputOutlined color="action" titleAccess={title} />
      case ExternalModbusDeviceIcon.generic:
        return <DeviceHubOutlined color="action" titleAccess={title} />
    }
  }

  function renderNodeIcon(row: DeviceNode) {
    if (row.type === DeviceNodeType.ExternalModbusDevice) {
      return renderExternalDeviceIcon(row)
    }
    if (isTillquistSettingsNode(row)) {
      // Muted, because this row is settings belonging to the Tillquist meter above it rather than a
      // device of its own, and like the read-only rows it has no edit action.
      return <SettingsOutlined color="action" />
    }
    return <ElectricMeterOutlined titleAccess={t('controllers.configuration.power_meters.icon_title')} />
  }

  function nodeActionsProps(row: DeviceNode): NodeActionsProps {
    switch (row.type) {
      case DeviceNodeType.Tillquist:
        return {
          editAction: () => tillquistDrawer.show(),
          editTitle: t('controllers.configuration.power_meters.tillquist.edit'),
        }
      case DeviceNodeType.AppConfigPowerMeter:
      case DeviceNodeType.ExternalModbusDevice:
        // Configured outside the part of raspi.properties this tree edits
        return {}
      case DeviceNodeType.LegacyModbus:
        return {
          editAction: () => modbusMeterDrawer.show(row),
          editTitle: t('controllers.configuration.power_meters.modbus.edit'),
          menuActions: [
            {
              icon: <ControlPointDuplicateOutlined />,
              label: 'Clone this Modbus meter',
              perform: async () => cloneModbusMeter(row),
              type: NodeMenuItemType.action,
            },
            {
              icon: <DeleteOutlined />,
              label: 'Remove this Modbus meter',
              perform: async () => removeModbusMeter(row),
              type: NodeMenuItemType.action,
            },
          ],
        }
    }
  }

  const columns: GridColDef<DeviceNode>[] = [
    {
      field: 'label',
      flex: 1,
      renderCell: ({ row, value }) => (
        <Stack alignItems="center" direction="row" gap={1}>
          {renderNodeIcon(row)}
          {row.type === DeviceNodeType.ExternalModbusDevice ? describeExternalModbusDevice(row.config, t) : value}
        </Stack>
      ),
    },
    {
      field: 'properties',
      flex: 3,
      valueGetter: (_, v: DeviceNode) => {
        let props: (string | undefined | false | 0)[] = []
        switch (v.type) {
          case DeviceNodeType.Tillquist: {
            props = describeTillquistProperties(v.config)
            break
          }
          case DeviceNodeType.AppConfigPowerMeter: {
            props = describeAppConfigPowerMeterProperties(v.config)
            break
          }
          case DeviceNodeType.LegacyModbus: {
            props = describeModbusMeterProperties(v.config)
            break
          }
          case DeviceNodeType.ExternalModbusDevice: {
            // Blank until RES-2526 summarises each family's own settings. The greyed icon and the
            // absent edit button already say the device is configured outside this tree.
            props = []
            break
          }
        }
        return props.filter(Boolean).join(', ')
      },
    },
    {
      field: 'errors',
      minWidth: 58,
      maxWidth: 58,
      renderCell: ({ row }) => <NodeErrors errors={row.errors} />,
    },
    {
      field: 'actions',
      // We can't allow flex width as that could hide the important buttons
      maxWidth: 102,
      minWidth: 102,
      renderCell: ({ row }) => <NodeActions {...nodeActionsProps(row)} />,
    },
  ]

  return (
    <Box ml={6}>
      {/* An empty port shows no grid: its "No rows" placeholder would only add height above the add buttons */}
      {deviceNodes.length > 0 && (
        <CustomDataGrid
          columnHeaderHeight={0}
          columns={columns}
          disableColumnReorder={true}
          disableColumnSorting={true}
          getRowId={(device: DeviceNode) => device.id}
          hideFooter={true}
          includeWrapper={false}
          rows={deviceNodes}
        />
      )}
      {/* With nothing to add either, a line of text keeps the expanded port from looking unexpandable */}
      {deviceNodes.length === 0 && !canAddDevice && (
        <CustomTypography color="text.secondary" padding={1}>
          No devices configured
        </CustomTypography>
      )}
      <Stack direction="row">
        {canAddTillquist && (
          <Button onClick={() => tillquistDrawer.show()}>
            <Chip
              color="default"
              icon={<AddOutlined />}
              label={t('controllers.configuration.power_meters.tillquist.label')}
              variant="filled"
            />
          </Button>
        )}
        {canAddAppConfigPowerMeter && (
          <Button onClick={() => appConfigPowerMeterDrawer.show()}>
            <Chip
              color="default"
              icon={<AddOutlined />}
              label={
                hasTillquistPowerMeterConfigured
                  ? t('controllers.configuration.power_meters.tillquist_other.label')
                  : t('controllers.configuration.power_meters.other.label')
              }
              variant="filled"
            />
          </Button>
        )}
        {supportsModbus && (
          <Button onClick={addNewModbusMeter}>
            <Chip color="default" icon={<AddOutlined />} label="Modbus meter" variant="filled" />
          </Button>
        )}
      </Stack>
      {configuration.raspiPropertiesConfig && tillquistDrawer.showing && (
        <TillquistPowerMeterDrawer
          config={configuration.raspiPropertiesConfig.tillquistConfig}
          open={tillquistDrawer.isOpen}
          onClose={tillquistDrawer.close}
          onConfigUpdated={onTillquistConfigurationUpdated}
          onFinishedClosing={tillquistDrawer.forgetContent}
        />
      )}
      {configuration.appConfig && appConfigPowerMeterDrawer.showing && (
        <AppConfigPowerMeterDrawer
          config={configuration.appConfig.powerMeters?.at(0)}
          open={appConfigPowerMeterDrawer.isOpen}
          onClose={appConfigPowerMeterDrawer.close}
          onConfigUpdated={onAppConfigPowerMeterUpdated}
          onFinishedClosing={appConfigPowerMeterDrawer.forgetContent}
        />
      )}
      {modbusMeterDrawer.showing && (
        <ModbusMeterSettingsDrawer
          config={modbusMeterDrawer.showing.content}
          connectionNode={connectionNode}
          open={modbusMeterDrawer.isOpen}
          onClose={modbusMeterDrawer.close}
          onConfigUpdated={onModbusMeterConfigurationUpdated}
          onFinishedClosing={modbusMeterDrawer.forgetContent}
        />
      )}
    </Box>
  )
}
